import {
	IconCheck,
	IconClock,
	IconLink,
	IconMinus,
	IconPencil,
	IconX,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { cn } from "@/lib/utils";
import { MARKETING_SECTION_ANIM_BASE } from "@/utils/animation";
import { seo } from "@/utils/seo";

const faqs = [
	{
		question: "Is 10MinuteMail free?",
		answer:
			"Yes, 10MinuteMail is completely free with no registration required. ineedatest.email is also completely free — no account, no paid tiers.",
	},
	{
		question: "Can I extend a 10MinuteMail address beyond 10 minutes?",
		answer:
			"Yes, in increments of 10 minutes by clicking a button on the page, up to a cap of roughly 100 minutes. The catch: you must be on the page to click the extend button before time runs out, and if you close the tab the session is gone. ineedatest.email lets you set any duration upfront — hours or days — and the inbox is accessible by URL regardless of whether you kept the tab open.",
	},
	{
		question: "What happens if I close my 10MinuteMail tab?",
		answer:
			"If you close the tab without bookmarking the address, the session is lost. A new visit to 10minutemail.com generates a completely different address. ineedatest.email inboxes are URL-based: as long as you have the inbox URL, you can come back to it from any browser until the expiry you set.",
	},
	{
		question: "Can I choose my own 10MinuteMail address?",
		answer:
			"No — 10MinuteMail assigns a random address. You cannot set the local part. ineedatest.email lets you type in the local part you want before creating the address, which is useful for predictable multi-step sign-up flows or when you need the same address across two devices.",
	},
	{
		question: "Is 10 minutes enough time to receive a verification email?",
		answer:
			"Often, but not always. Verification and OTP emails from large platforms typically arrive in under a minute. But some services — especially smaller SaaS products, legacy systems, or queued transactional email — can take 3 to 8 minutes to deliver. That leaves very little margin. ineedatest.email lets you set a longer window upfront so you are not racing a countdown while waiting for a slow sender.",
	},
];

const comparisonRows: {
	feature: string;
	ours: { state: "yes" | "no" | "partial"; note?: string };
	theirs: { state: "yes" | "no" | "partial"; note?: string };
}[] = [
	{
		feature: "Completely free",
		ours: { state: "yes" },
		theirs: { state: "yes" },
	},
	{
		feature: "Account required",
		ours: { state: "no" },
		theirs: { state: "no" },
	},
	{
		feature: "User-controlled expiry",
		ours: { state: "yes", note: "Set any duration upfront" },
		theirs: { state: "no", note: "Fixed 10 min, extend to ~100 min max" },
	},
	{
		feature: "Inbox persists after closing tab",
		ours: { state: "yes", note: "URL-based, not session-based" },
		theirs: { state: "no", note: "Session lost if tab is closed" },
	},
	{
		feature: "Choose your own local part",
		ours: { state: "yes", note: "Type what you want" },
		theirs: { state: "no", note: "Random address only" },
	},
	{
		feature: "Multiple simultaneous inboxes",
		ours: { state: "yes", note: "Each URL is a separate inbox" },
		theirs: { state: "no", note: "One address per session" },
	},
	{
		feature: "Outbound sending",
		ours: { state: "no", note: "Inbound-only by design" },
		theirs: { state: "no" },
	},
	{
		feature: "Attachment storage",
		ours: { state: "no", note: "Text and HTML bodies only" },
		theirs: { state: "no" },
	},
	{
		feature: "Multiple domains",
		ours: { state: "yes" },
		theirs: { state: "partial", note: "Limited domain options" },
	},
	{
		feature: "API access",
		ours: { state: "no" },
		theirs: { state: "no" },
	},
	{
		feature: "Blocked by major services",
		ours: { state: "partial", note: "Less common" },
		theirs: { state: "yes", note: "Well-known, widely blocked" },
	},
	{
		feature: "Mobile app",
		ours: { state: "no" },
		theirs: { state: "no" },
	},
];

const switchReasons = [
	{
		icon: IconClock,
		title: "A timer you set, not one counting down against you",
		body: "10 minutes sounds like enough — until you are waiting for an email from a slow sender, or you get pulled away before you click the verification link. ineedatest.email lets you set the expiry when you create the address: 15 minutes, 2 hours, a full day. The clock starts when you decide it starts, and you are not racing to beat it.",
	},
	{
		icon: IconLink,
		title: "The inbox lives at a URL, not inside a browser tab",
		body: "10MinuteMail is session-based. Close the tab, refresh to a new page, or lose your connection, and the address is gone — a new visit gives you a completely different inbox. ineedatest.email inboxes are addressable by URL. Copy the link, open it on your phone, come back to it an hour later from a different browser. The inbox is wherever the URL is.",
	},
	{
		icon: IconPencil,
		title: "Pick the address you want",
		body: "10MinuteMail assigns a random string you did not choose and may not be able to re-type from memory. ineedatest.email lets you type in the local part before creating the address — something short, something you can remember, or something domain-specific for the service you are signing up for. You get the inbox at the exact address you typed.",
	},
	{
		icon: IconCheck,
		title: "Works for multi-step and multi-device flows",
		body: "Many sign-up flows require more than one step: confirm your email, then verify a phone number, then log in from a link. 10MinuteMail gives you one address and one session. ineedatest.email gives you a URL you can open on a second device, share with a colleague, or return to after a longer wait — without re-entering anything.",
	},
];

const honestyRows = [
	{
		feature: "Absolute simplicity — zero decisions",
		body: "10MinuteMail has one interface: you land on the page and an inbox is live. No timer to set, no local part to choose, nothing to configure. For a single OTP from a fast sender, that frictionlessness is a genuine advantage. ineedatest.email asks you to pick a domain and set a timer — two extra steps that take a few seconds but add friction that not everyone wants.",
	},
	{
		feature: "Predictable, well-known behavior",
		body: "10MinuteMail has existed long enough that most developers and privacy-conscious users know exactly what it does. The product has essentially one behavior: give me a throwaway inbox for 10 minutes. That predictability has value — there is no ambiguity about what you are getting. ineedatest.email is newer and less tested in the wild.",
	},
	{
		feature: "No URL to remember or manage",
		body: "Because 10MinuteMail is session-based, there is nothing to keep track of after you are done. The address expires, the session ends, and there is no inbox URL floating around that could theoretically be visited later. ineedatest.email's URL-persistence is a feature for multi-step use — but for a truly single-use OTP with no return visit needed, the session model has a clean finality to it.",
	},
];

function StateCell({
	state,
	note,
}: {
	state: "yes" | "no" | "partial";
	note?: string;
}) {
	return (
		<div className="flex flex-col gap-0.5">
			<span className="flex items-center gap-1.5">
				{state === "yes" && (
					<IconCheck className="size-4 shrink-0 text-green-600 dark:text-green-500" />
				)}
				{state === "no" && (
					<IconX className="size-4 shrink-0 text-destructive" />
				)}
				{state === "partial" && (
					<IconMinus className="size-4 shrink-0 text-amber-500" />
				)}
				<span className="text-xs font-medium text-foreground capitalize">
					{state === "partial" ? "limited" : state}
				</span>
			</span>
			{note && (
				<span className="text-xs text-muted-foreground leading-snug">
					{note}
				</span>
			)}
		</div>
	);
}

export const Route = createFileRoute("/_marketing/alternatives/10minutemail")({
	component: RouteComponent,
	head: () =>
		seo({
			title: "10MinuteMail alternative — ineedatest.email",
			description:
				"Disposable inboxes with expiry you control, a URL you keep, and no 10-minute race. See how ineedatest.email compares to 10MinuteMail.",
			keywords:
				"10minutemail alternative, 10 minute mail alternatives, alternative to 10minutemail, disposable email, temporary email",
			type: "FAQPage",
			faqItems: faqs,
		}),
});

export function RouteComponent() {
	return (
		<MarketingShell>
			{/* Hero */}
			<section className="pb-12 pt-20 sm:pb-16 sm:pt-28">
				<MarketingContainer>
					<nav className="mb-6 text-xs text-muted-foreground">
						<a href="/" className="hover:text-foreground">
							ineedatest.email
						</a>
						<span className="mx-2">/</span>
						<span>10MinuteMail alternative</span>
					</nav>
					<div className={cn("max-w-3xl", MARKETING_SECTION_ANIM_BASE)}>
						<p className="mb-3 text-sm font-medium uppercase tracking-wider text-primary">
							10MinuteMail alternative
						</p>
						<h1 className="mb-4 text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
							Looking for a 10MinuteMail alternative?
						</h1>
						<p className="text-lg text-pretty text-muted-foreground sm:text-xl">
							10 minutes is often not enough — and closing the tab means
							starting over. ineedatest.email gives you a URL-based inbox with
							expiry you set, so you are not racing a countdown while waiting
							for a slow sender.
						</p>
					</div>
				</MarketingContainer>
			</section>

			{/* Comparison table */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Comparison"
						heading="Best 10 minute mail alternatives in 2026"
						description="How ineedatest.email stacks up against 10MinuteMail for receiving OTPs, sign-up verifications, and any flow where the 10-minute window is too short or too rigid."
						className="mb-10 sm:mb-12"
					/>
					<div className="overflow-x-auto rounded-xl border border-border/60">
						<table className="w-full min-w-[560px] text-sm">
							<thead>
								<tr className="border-b border-border/60 bg-muted/30">
									<th className="py-3 pl-5 pr-4 text-left font-semibold text-foreground">
										Feature
									</th>
									<th className="px-4 py-3 text-left font-semibold text-primary">
										ineedatest.email
									</th>
									<th className="px-4 py-3 text-left font-semibold text-foreground">
										10MinuteMail
									</th>
								</tr>
							</thead>
							<tbody>
								{comparisonRows.map((row, i) => (
									<tr
										key={row.feature}
										className={cn(
											"border-b border-border/40 last:border-0",
											i % 2 === 0 ? "bg-transparent" : "bg-muted/10",
										)}
									>
										<td className="py-3 pl-5 pr-4 font-medium text-foreground">
											{row.feature}
										</td>
										<td className="px-4 py-3">
											<StateCell {...row.ours} />
										</td>
										<td className="px-4 py-3">
											<StateCell {...row.theirs} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<p className="mt-3 text-xs text-muted-foreground">
						10MinuteMail features sourced from their public site. Accurate as of
						May 2026.
					</p>
				</MarketingContainer>
			</section>

			{/* Why switch */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Why switch"
						heading="When ineedatest.email fits better than 10MinuteMail"
						description="The 10-minute model works for instant OTPs from fast senders. Here is when it breaks down."
						className="mb-10 sm:mb-12"
					/>
					<div className="grid gap-6 sm:gap-8 lg:grid-cols-2">
						{switchReasons.map((reason, index) => {
							const Icon = reason.icon;
							return (
								<div
									key={reason.title}
									className={cn(
										"flex gap-5 rounded-xl border border-border/80 bg-card/30 p-6 ring-1 ring-border/40 sm:p-8",
										MARKETING_SECTION_ANIM_BASE,
									)}
									style={{ animationDelay: `${index * 0.1}s` }}
								>
									<div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/40">
										<Icon className="size-5 text-primary" aria-hidden />
									</div>
									<div className="flex min-w-0 flex-col gap-2">
										<h3 className="text-base font-semibold tracking-tight text-foreground">
											{reason.title}
										</h3>
										<p className="text-sm text-pretty text-muted-foreground">
											{reason.body}
										</p>
									</div>
								</div>
							);
						})}
					</div>
				</MarketingContainer>
			</section>

			{/* Honesty section */}
			<section className="border-t border-border/50 bg-muted/15 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Honest take"
						heading="Where 10MinuteMail still wins"
						description="Three situations where 10MinuteMail's constraints are actually advantages."
						className="mb-10 sm:mb-12"
					/>
					<div className="grid gap-5 sm:gap-6 lg:grid-cols-3">
						{honestyRows.map((row) => (
							<div
								key={row.feature}
								className="rounded-xl border border-border/60 bg-card/30 p-6 ring-1 ring-border/30"
							>
								<h3 className="mb-2 text-sm font-semibold text-foreground">
									{row.feature}
								</h3>
								<p className="text-sm text-pretty text-muted-foreground">
									{row.body}
								</p>
							</div>
						))}
					</div>
				</MarketingContainer>
			</section>

			{/* FAQ */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="FAQ"
						heading="Questions about switching from 10MinuteMail"
						className="mb-10 sm:mb-12"
					/>
					<div className="divide-y divide-border/50">
						{faqs.map((faq) => (
							<div key={faq.question} className="py-6 first:pt-0 last:pb-0">
								<h3 className="mb-2 text-base font-semibold text-foreground">
									{faq.question}
								</h3>
								<p className="text-sm text-pretty text-muted-foreground leading-relaxed">
									{faq.answer}
								</p>
							</div>
						))}
					</div>
				</MarketingContainer>
			</section>

			{/* CTA + sibling links */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<p className="text-base font-semibold text-foreground">
								Try ineedatest.email — set your own timer, keep your URL
							</p>
							<p className="mt-1 text-sm text-muted-foreground">
								No account. No countdown pressure. No session to lose.
							</p>
						</div>
						<a
							href="/"
							className="shrink-0 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
						>
							Get a disposable inbox
						</a>
					</div>

					<div className="mt-12 border-t border-border/50 pt-8">
						<p className="mb-3 text-sm text-muted-foreground">
							See what ineedatest.email can do:{" "}
							<a
								href="/features"
								className="text-primary underline-offset-4 hover:underline"
							>
								product features
							</a>
						</p>
						<p className="mb-4 text-sm font-medium text-muted-foreground">
							Compare other disposable email services
						</p>
						<div className="flex flex-wrap gap-3">
							<a
								href="/alternatives/mailinator"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								Mailinator alternatives
							</a>
							<a
								href="/alternatives/guerrilla-mail"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								Guerrilla Mail alternatives
							</a>
							<a
								href="/alternatives/temp-mail"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								Temp Mail alternatives
							</a>
						</div>
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
